import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { axiosInstance } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [passwords, setPasswords] = useState({
    new_password: "",
    confirm_password: ""
  });

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      toast.error("Token não encontrado");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error("As senhas não coincidem");
      return;
    }

    if (passwords.new_password.length < 6) {
      toast.error("A senha deve ter no mínimo 6 caracteres");
      return;
    }

    setLoading(true);
    
    try {
      await axiosInstance.post("/auth/reset-password", {
        token: token,
        new_password: passwords.new_password
      });
      
      setSuccess(true);
      toast.success("Senha alterada com sucesso!");
      
      // Redirecionar para login após 3 segundos
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao resetar senha";
      toast.error(message);
      
      if (message.includes("Token") || message.includes("expirado")) {
        setTokenValid(false);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="w-full max-w-md">
          <Card className="shadow-2xl border-0">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-red-500" />
                Token Inválido
              </CardTitle>
              <CardDescription>
                O link de reset de senha é inválido ou expirou
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  Este link pode ter expirado (válido por 1 hora) ou já foi utilizado.
                </p>
              </div>
              
              <Button
                onClick={() => navigate('/forgot-password')}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
              >
                Solicitar novo link
              </Button>
              
              <Button
                onClick={() => navigate('/login')}
                className="w-full"
                variant="outline"
              >
                Voltar para Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="w-full max-w-md">
          <Card className="shadow-2xl border-0">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-green-500" />
                Senha Alterada!
              </CardTitle>
              <CardDescription>
                Sua senha foi alterada com sucesso
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  Você será redirecionado para a página de login em alguns segundos...
                </p>
              </div>
              
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
              >
                Ir para Login agora
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50">
      <div className="w-full max-w-md">
        <Card className="shadow-2xl border-0">
          <CardHeader>
            <CardTitle className="text-2xl">Redefinir Senha</CardTitle>
            <CardDescription>
              Digite sua nova senha
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new_password">Nova Senha</Label>
                <Input
                  id="new_password"
                  type="password"
                  placeholder="••••••••"
                  value={passwords.new_password}
                  onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                  required
                  minLength={6}
                  autoFocus
                />
                <p className="text-xs text-gray-500">
                  Mínimo de 6 caracteres
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirmar Nova Senha</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  placeholder="••••••••"
                  value={passwords.confirm_password}
                  onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
              
              {passwords.confirm_password && passwords.new_password !== passwords.confirm_password && (
                <p className="text-sm text-red-600">
                  As senhas não coincidem
                </p>
              )}
              
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
                disabled={loading || passwords.new_password !== passwords.confirm_password}
              >
                {loading ? "Alterando..." : "Alterar Senha"}
              </Button>
              
              <Button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full"
                variant="outline"
              >
                Cancelar
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
